// What the admin should know before refunding a traveler, given where each
// trip's host payment stands. The rule (decided with the owner): a partial
// refund never changes what the host is owed, so it is Sekretly's cost; a full
// refund before the host is paid cancels the host's payment; and a full refund of
// a trip that is on a host invoice has to wait until that invoice is sent back.

export interface RefundItem {
  payout_status: string;
  amount_cents: number;
  refunded_amount_cents: number;
}

export interface RefundAdvice {
  /** Plain-language notes for the confirmation dialog. */
  notes: string[];
  /** A single refund that can't go ahead yet (the server refuses it too). */
  blocked: boolean;
}

/**
 * `singleAmountCents` is the amount typed for a one-payment refund; a bulk refund
 * always refunds everything that is left on each payment.
 */
export function refundAdvice(items: RefundItem[], singleAmountCents?: number): RefundAdvice {
  const notes: string[] = [];
  const single = items.length === 1 && singleAmountCents !== undefined;
  const isFull = (i: RefundItem) => {
    const remaining = i.amount_cents - i.refunded_amount_cents;
    return !single || singleAmountCents! >= remaining;
  };

  const invoicedFull = items.filter((i) => i.payout_status === 'invoiced' && isFull(i));
  const paidToHost = items.filter((i) => i.payout_status === 'paid');
  const partialOwed = items.filter((i) => !isFull(i) && (i.payout_status === 'held' || i.payout_status === 'failed' || i.payout_status === 'invoiced'));
  const fullCancels = items.filter((i) => isFull(i) && (i.payout_status === 'held' || i.payout_status === 'failed' || i.payout_status === 'processing'));

  if (invoicedFull.length > 0) {
    notes.push(items.length === 1
      ? 'This trip is on a host invoice, so it can’t be refunded in full yet. Send the invoice back first (Host invoices page), then refund it.'
      : `${invoicedFull.length} of these trips are on a host invoice and can’t be refunded in full yet. They will be skipped: send their invoices back first.`);
  }
  if (paidToHost.length > 0) {
    notes.push('The host has already been paid for this trip. The refund comes out of Sekretly’s balance and doesn’t change their payment.');
  }
  if (partialOwed.length > 0) {
    notes.push('A partial refund doesn’t change what the host is owed: the host still gets their full fee, so it is Sekretly’s cost.');
  }
  if (fullCancels.length > 0) {
    notes.push('A full refund cancels the host’s payment for this trip: they won’t be able to invoice it.');
  }
  return { notes, blocked: single && invoicedFull.length > 0 };
}
